import React, { useState } from 'react';
import {
  Trophy,
  Medal,
  Flame,
  PlusCircle,
  Search,
  Award,
  CheckCircle2,
} from 'lucide-react';
import { UI_TRANSLATIONS, SupportedLanguage } from '../i18n/translations.ts';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import { AdSenseBanner } from './AdSenseBanner.tsx';
import { CollapsibleSelect } from './ui/CollapsibleSelect.tsx';
import { FriendButton } from './FriendButton.tsx';
import {
  SCORING_CONFIG,
  formatDisplayNumber,
  compareLeaderboardEntries,
  compareTopAccuracyEntries,
} from '../config/scoringConfig.ts';
import { RegistrationRequiredCard } from './RegistrationRequiredCard.tsx';

type PeriodTab =
  | 'weekly'
  | 'monthly'
  | 'alltime'
  | 'country'
  | 'accuracy'
  | 'tournaments';

const PERIOD_OPTIONS: readonly { id: PeriodTab; label: string }[] = [
  { id: 'weekly', label: 'This Week' },
  { id: 'monthly', label: 'This Month' },
  { id: 'alltime', label: 'All-Time' },
  { id: 'country', label: 'By Country' },
  { id: 'accuracy', label: 'Top Accuracy' },
  { id: 'tournaments', label: 'Tournaments' },
];

interface RankingsAndTournamentsViewProps {
  isRegistered?: boolean;
  userUid: string;
  username: string;
  country: string;
  language: SupportedLanguage;
  leaderboards: any[];
  tournaments: any[];
  onRefreshData: () => void;
  onPlayTournamentQuiz: (category: string, difficulty: string) => void;
  onOpenUserProfile?: (uidOrUsername: string) => void;
  onNavigateToProfile?: (subTab?: string) => void;
}

export const RankingsAndTournamentsView: React.FC<
  RankingsAndTournamentsViewProps
> = ({
  isRegistered = false,
  userUid,
  username,
  country,
  language,
  leaderboards,
  tournaments,
  onRefreshData,
  onPlayTournamentQuiz,
  onOpenUserProfile,
  onNavigateToProfile,
}) => {
  if (!isRegistered) {
    return (
      <RegistrationRequiredCard
        title="Global Leaderboards & Tournaments"
        description="Create a free Quick!Bible account to view international rankings, participate in scripture tournaments, and compete with Bible scholars worldwide."
        onSignUp={() => onNavigateToProfile?.('auth_signup')}
        onSignIn={() => onNavigateToProfile?.('auth_signin')}
      />
    );
  }
  const t = UI_TRANSLATIONS[language] || UI_TRANSLATIONS.en;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('qb_auth_token') || '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const [periodTab, setPeriodTab] = useState<PeriodTab>(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.toLowerCase();
      if (hash.includes('weekly')) return 'weekly';
      if (hash.includes('monthly')) return 'monthly';
      if (hash.includes('country')) return 'country';
      if (hash.includes('accuracy')) return 'accuracy';
      if (hash.includes('tournament')) return 'tournaments';
      if (hash.includes('alltime')) return 'alltime';
      const stored = sessionStorage.getItem('qb_ranks_subtab');
      if (
        stored &&
        ['weekly', 'monthly', 'alltime', 'country', 'accuracy', 'tournaments'].includes(
          stored
        )
      ) {
        return stored as PeriodTab;
      }
    }
    return 'alltime';
  });

  const handlePeriodTabChange = (tab: PeriodTab) => {
    soundEngine.playClick();
    setPeriodTab(tab);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('qb_ranks_subtab', tab);
      window.history.replaceState({}, '', `#ranks-${tab}`);
    }
  };
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Create Church Tournament Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [trnTitle, setTrnTitle] = useState('');
  const [trnOrg, setTrnOrg] = useState('');
  const [trnCategory, setTrnCategory] = useState('ALL CATEGORIES');
  const [trnDifficulty, setTrnDifficulty] = useState('Intermediate');
  const [isSubmittingTrn, setIsSubmittingTrn] = useState(false);

  const getEntryQuestionsSeen = (entry: any): number => {
    if (typeof entry.questionsAnswered === 'number') return entry.questionsAnswered;
    if (typeof entry.questionsSeen === 'number') return entry.questionsSeen;
    if (typeof entry.totalQuestionsAnswered === 'number') {
      return entry.totalQuestionsAnswered;
    }
    return Math.max(0, Number(entry.gamesPlayed || 0) * 5);
  };

  const sortedLeaders = [...leaderboards]
    .filter((entry) => {
      const uname = (entry.username || entry.display_name || '').toLowerCase();
      return true;
      if (periodTab !== 'accuracy' && Number(entry.totalPoints ?? entry.total_points ?? 0) <= 0) {
        return false;
      }
      if (
        periodTab === 'country' &&
        countryFilter !== 'ALL' &&
        entry.country !== countryFilter
      ) {
        return false;
      }
      if (
        periodTab === 'accuracy' &&
        getEntryQuestionsSeen(entry) <
          SCORING_CONFIG.ACCURACY.TOP_ACCURACY_BOARD_MIN_QUESTIONS
      ) {
        return false;
      }
      if (
        searchQuery.trim() &&
        !String(entry.username || '')
          .toLowerCase()
          .includes(searchQuery.trim().toLowerCase())
      ) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (periodTab === 'accuracy') {
        return compareTopAccuracyEntries(
          {
            accuracy: Number(a.accuracy || 0),
            xp: Number(a.totalPoints || 0),
            updatedAt: a.updatedAt,
          },
          {
            accuracy: Number(b.accuracy || 0),
            xp: Number(b.totalPoints || 0),
            updatedAt: b.updatedAt,
          }
        );
      }

      const xpA =
        periodTab === 'weekly'
          ? Number(a.weeklyPoints || 0)
          : periodTab === 'monthly'
          ? Number(a.monthlyPoints || 0)
          : Number(a.totalPoints || 0);
      const xpB =
        periodTab === 'weekly'
          ? Number(b.weeklyPoints || 0)
          : periodTab === 'monthly'
          ? Number(b.monthlyPoints || 0)
          : Number(b.totalPoints || 0);

      return compareLeaderboardEntries(
        { xp: xpA, accuracy: Number(a.accuracy || 0), updatedAt: a.updatedAt },
        { xp: xpB, accuracy: Number(b.accuracy || 0), updatedAt: b.updatedAt }
      );
    });

  const handleCreateTournament = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trnTitle.trim() || !trnOrg.trim()) return;
    setIsSubmittingTrn(true);
    try {
      await fetch('/api/tournaments', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'create',
          userUid,
          username,
          country,
          title: trnTitle.trim(),
          organization: trnOrg.trim(),
          category: trnCategory,
          difficulty: trnDifficulty,
        }),
      });
      setShowCreateModal(false);
      setTrnTitle('');
      setTrnOrg('');
      onRefreshData();
    } finally {
      setIsSubmittingTrn(false);
    }
  };

  const handleJoinAndCompete = async (trn: any) => {
    soundEngine.playClick();
    await fetch('/api/tournaments', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        action: 'join',
        userUid,
        username,
        country,
        tournamentId: trn.id,
      }),
    });
    onRefreshData();
    onPlayTournamentQuiz(trn.category, trn.difficulty);
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#FAFAFA]">
            {t.globalLeaderboards}
          </h1>
          <p className="text-sm text-[#737373] text-[#A3A3A3] mt-1">
            Server-verified rankings by XP across Weekly, Monthly, All-Time,
            Country, Top Accuracy (min. 30 questions), and Church Tournaments.
          </p>
        </div>

        {/* Desktop Filter Tabs (1024px and up / hidden lg:flex) */}
        <div className="hidden lg:flex items-center gap-1.5 p-1.5 rounded-2xl bg-[#141414] border border-[#262626]">
          {PERIOD_OPTIONS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-testid={`ranks-tab-${tab.id}`}
              onClick={() => handlePeriodTabChange(tab.id)}
              className={`px-3.5 py-2 min-h-[38px] rounded-xl text-xs font-semibold transition-all text-center flex items-center justify-center cursor-pointer ${
                periodTab === tab.id
                  ? 'bg-[#1C1C1C] text-white shadow-sm font-bold ring-1 ring-[#525252]'
                  : 'text-[#A3A3A3] hover:text-white'
              }`}
            >
              <span className="truncate">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile/Tablet Collapsible Selector (below 1024px / lg:hidden) */}
      <div className="w-full lg:hidden mb-6">
        <CollapsibleSelect<PeriodTab>
          label="Ranking Period"
          options={PERIOD_OPTIONS}
          value={periodTab}
          onChange={(val) => handlePeriodTabChange(val)}
        />
      </div>

      {periodTab === 'tournaments' ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#FAFAFA]">
              Active Church & Organization Tournaments
            </h2>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold transition-colors"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Organize Church Tournament</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tournaments.map((trn) => (
              <div
                key={trn.id}
                className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="text-xs text-[#A3A3A3] font-medium mb-1">
                    {trn.organization} · {trn.category} · {trn.difficulty}
                  </div>
                  <h3 className="text-xl font-bold text-[#FAFAFA] mb-2">
                    {trn.title}
                  </h3>
                  <p className="text-xs text-[#737373] text-[#A3A3A3] mb-4 leading-relaxed">
                    {trn.description}
                  </p>

                  <div className="space-y-2 mb-6">
                    <div className="flex items-center justify-between text-xs font-semibold text-[#D4D4D4]">
                      <span>Current Tournament Standings</span>
                      <span className="text-[11px] font-mono text-[#737373]">Score</span>
                    </div>
                    {trn.standings?.slice(0, 3).map((st: any, idx: number) => {
                      const fmtScore = formatDisplayNumber(st.score);
                      return (
                        <div
                          key={st.id}
                          className="flex items-center justify-between text-xs py-1.5 border-b border-[#262626]"
                        >
                          <span className="font-medium text-[#FAFAFA]">
                            #{idx + 1} {st.username} ({st.country})
                          </span>
                          <span className="font-mono font-bold text-[#FAFAFA] tabular-nums">
                            <span className={fmtScore.isZero ? 'text-[#737373]' : 'text-[#FAFAFA]'}>
                              {fmtScore.text}
                            </span>
                            <span className="text-[11px] text-[#737373] font-normal ml-1.5">
                              {st.accuracy}%
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-[#262626]">
                  <div className="text-xs text-[#737373]">
                    Champion Title: <strong>{trn.prizeBadge}</strong>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleJoinAndCompete(trn)}
                    className="px-4 py-2 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold transition-colors"
                  >
                    Enter & Play Round
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Top 3 Champions Podium */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {sortedLeaders.slice(0, 3).map((champ, idx) => {
              const xpVal =
                periodTab === 'weekly'
                  ? Number(champ.weeklyPoints || 0)
                  : periodTab === 'monthly'
                  ? Number(champ.monthlyPoints || 0)
                  : Number(champ.totalPoints || 0);
              const fmtXp = formatDisplayNumber(xpVal);
              const fmtWins = formatDisplayNumber(champ.wins || 0);
              const rankColors = [
                'text-amber-500',
                'text-[#A3A3A3]',
                'text-amber-400',
              ];
              const rankLabels = [
                '1st · Gold Champion',
                '2nd · Silver Laureate',
                '3rd · Bronze Scholar',
              ];

              return (
                <div
                  key={champ.userUid}
                  className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <div
                        className={`text-xs font-bold flex items-center gap-1.5 ${rankColors[idx]}`}
                      >
                        <Medal className="w-4 h-4" />
                        <span>{rankLabels[idx]}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <button
                          type="button"
                          onClick={() =>
                            onOpenUserProfile &&
                            onOpenUserProfile(champ.userUid || champ.username)
                          }
                          className="text-lg font-bold text-[#FAFAFA] hover:text-amber-400 transition-colors cursor-pointer text-left truncate"
                        >
                          {champ.username}
                        </button>
                        {userUid &&
                          champ.userUid &&
                          champ.userUid !== userUid && (
                            <FriendButton
                              currentUserUid={userUid}
                              targetUserUid={champ.userUid}
                              size="xs"
                            />
                          )}
                      </div>
                      <div className="text-xs text-[#737373]">
                        {champ.titleBadge} · Country: {champ.country}
                      </div>
                    </div>
                    <div
                      onClick={() =>
                        onOpenUserProfile &&
                        onOpenUserProfile(champ.userUid || champ.username)
                      }
                      className="w-10 h-10 rounded-full bg-[#1C1C1C] hover:border-amber-500/40 border border-[#262626] text-[#A3A3A3] hover:text-amber-400 font-bold text-sm flex items-center justify-center shrink-0 cursor-pointer transition-colors"
                      title={`View @${champ.username}'s profile`}
                    >
                      {champ.username.slice(0, 2).toUpperCase()}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#262626] flex items-center justify-between">
                    <div>
                      <div className="text-[11px] text-[#737373]">XP</div>
                      <div
                        className={`text-xl font-mono font-bold tabular-nums ${
                          fmtXp.isZero ? 'text-[#737373]' : 'text-[#FAFAFA]'
                        }`}
                      >
                        {fmtXp.text}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] text-[#737373]">
                        Accuracy & Wins
                      </div>
                      <div className="text-xs font-mono text-[#737373] tabular-nums">
                        {champ.accuracy}% ·{' '}
                        <span className={fmtWins.isZero ? 'text-[#737373]' : 'text-[#A3A3A3]'}>
                          {fmtWins.isZero ? '–' : `${fmtWins.text}W`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Search & Country Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search player username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search player username"
                className="w-full pl-9 pr-4 py-2 rounded-xl border border-[#262626] bg-[#141414] text-xs text-[#FAFAFA]"
              />
            </div>

            {periodTab === 'accuracy' && (
              <div className="text-xs text-[#737373] font-medium">
                Minimum {SCORING_CONFIG.ACCURACY.TOP_ACCURACY_BOARD_MIN_QUESTIONS}{' '}
                questions answered required
              </div>
            )}

            {periodTab === 'country' && (
              <div className="flex items-center gap-2">
                {['ALL', 'PH', 'US', 'BR', 'NG'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCountryFilter(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                      countryFilter === c
                        ? 'bg-[#525252] text-white'
                        : 'border border-[#262626] text-[#D4D4D4]'
                    }`}
                  >
                    {c === 'ALL' ? 'All Countries' : c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* High-Density Data Table — XP shown once in column header, accuracy as small gray text */}
          <div className="rounded-2xl bg-[#141414] border border-[#262626] overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse" data-testid="rankings-table">
                <thead>
                  <tr className="border-b border-[#262626] text-[11px] font-semibold uppercase tracking-wider text-[#737373] text-[#A3A3A3]">
                    <th className="py-3.5 px-4">Rank</th>
                    <th className="py-3.5 px-4">Player</th>
                    <th className="py-3.5 px-4">Country</th>
                    <th className="py-3.5 px-4 text-right">Games</th>
                    <th className="py-3.5 px-4 text-right">Wins</th>
                    <th className="py-3.5 px-4 text-right">Best Streak</th>
                    <th className="py-3.5 px-4 text-right" data-testid="rankings-xp-header">
                      XP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262626] text-sm">
                  {sortedLeaders.map((row, index) => {
                    const xpVal =
                      periodTab === 'weekly'
                        ? Number(row.weeklyPoints || 0)
                        : periodTab === 'monthly'
                        ? Number(row.monthlyPoints || 0)
                        : Number(row.totalPoints || 0);

                    const fmtXp = formatDisplayNumber(xpVal);
                    const fmtGames = formatDisplayNumber(row.gamesPlayed || 0);
                    const fmtWins = formatDisplayNumber(row.wins || 0);
                    const fmtStreak = formatDisplayNumber(row.bestStreak || 0);

                    return (
                      <tr
                        key={row.userUid}
                        className="hover:bg-[#1C1C1C] transition-colors"
                      >
                        <td className="py-3 px-4 font-mono font-bold text-[#FAFAFA] tabular-nums">
                          #{index + 1}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() =>
                                onOpenUserProfile &&
                                onOpenUserProfile(row.userUid || row.username)
                              }
                              className="font-semibold text-[#FAFAFA] hover:text-amber-400 transition-colors cursor-pointer text-left truncate"
                            >
                              {row.username}
                            </button>
                            {userUid &&
                              row.userUid &&
                              row.userUid !== userUid && (
                                <FriendButton
                                  currentUserUid={userUid}
                                  targetUserUid={row.userUid}
                                  size="xs"
                                />
                              )}
                          </div>
                          <div className="text-xs text-[#737373]">
                            {row.titleBadge}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono text-xs text-[#D4D4D4]">
                          {row.country}
                        </td>
                        <td
                          className={`py-3 px-4 text-right font-mono tabular-nums ${
                            fmtGames.isZero ? 'text-[#737373]' : 'text-[#D4D4D4]'
                          }`}
                        >
                          {fmtGames.text}
                        </td>
                        <td
                          className={`py-3 px-4 text-right font-mono tabular-nums ${
                            fmtWins.isZero ? 'text-[#737373]' : 'text-[#D4D4D4]'
                          }`}
                        >
                          {fmtWins.text}
                        </td>
                        <td
                          className={`py-3 px-4 text-right font-mono tabular-nums ${
                            fmtStreak.isZero ? 'text-[#737373]' : 'text-amber-500'
                          }`}
                        >
                          {fmtStreak.text}
                        </td>
                        <td className="py-3 px-4 text-right font-mono tabular-nums">
                          <div
                            className={`font-bold ${
                              fmtXp.isZero ? 'text-[#737373]' : 'text-[#FAFAFA]'
                            }`}
                          >
                            {fmtXp.text}
                          </div>
                          <div className="text-xs text-[#737373] font-normal">
                            {row.accuracy ?? 0}%
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create Church Tournament Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
        >
          <form
            onSubmit={handleCreateTournament}
            className="w-full max-w-md rounded-2xl bg-[#141414] border border-[#262626] p-6 space-y-4"
          >
            <h3 className="text-lg font-bold text-[#FAFAFA]">
              Organize Church / Fellowship Tournament
            </h3>
            <div>
              <label
                htmlFor="trn-title-input"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1"
              >
                Tournament Name
              </label>
              <input
                id="trn-title-input"
                type="text"
                required
                placeholder="e.g., National Youth Scripture Cup"
                value={trnTitle}
                onChange={(e) => setTrnTitle(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2 text-sm text-[#FAFAFA]"
              />
            </div>
            <div>
              <label
                htmlFor="trn-org-input"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1"
              >
                Host Church or Organization
              </label>
              <input
                id="trn-org-input"
                type="text"
                required
                placeholder="e.g., Grace Fellowship Manila"
                value={trnOrg}
                onChange={(e) => setTrnOrg(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2 text-sm text-[#FAFAFA]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 rounded-xl border border-[#262626] text-xs font-medium text-[#D4D4D4]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingTrn}
                className="px-4 py-2 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold"
              >
                {isSubmittingTrn ? 'Creating...' : 'Launch Tournament'}
              </button>
            </div>
          </form>
        </div>
      )}

      <AdSenseBanner placement="rankings" className="mt-8" />
    </div>
  );
};
